import React, {useCallback, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useStoreActions, useStoreState} from '~/store';
import {LastPeriodicCheck, OperatingUnitTypePayload, Project} from "~/types";
import Header from "./Header";
import List, {ListProps} from "./List";
import useTranslation from "~/app/hooks/useTranslation";
import moment from 'moment';
import RegisterOperatingTime from '~/operatingTime/Register';
import AddIssue, {AddIssueProps} from '~/issues/Add';
import {ItemProps} from "./List/ListItem";
import {route} from "~/app/helpers/route";
import {MACHINES_DETAILS_ROUTES} from "~/machines/Details/Router";
import {useHistory} from "react-router";
import {PERMISSION_GROUPS} from "~/accessControl/permissionGroups";
import useAccessControl from "~/app/hooks/useAccessControl";
import {InspectionTypes} from "~/periodicCheck/store";
import { OPERATING_COUNT_UNIT_NAME, OperatingTypes } from '~/app/data/statuses/operatingData';
import Loader from '~/app/common/Loader';
import useDidMountEffect from '~/app/hooks/useDidMountEffect';
import { PERIODIC_CHECK_DRAFT_STATUS } from '~/app/data/statuses/periodicCheck';
import { isPeriodicCheckAwaitingSync, usePendingPeriodicChecks } from '~/periodicCheck/usePendingPeriodicChecks';
import { usePendingOperatingData } from '~/operatingTime/usePendingOperatingData';

export type RegistrationComponent = React.FC<Pick<ListProps, 'onSelect'>>

const Registration: RegistrationComponent = () => {
    const history = useHistory();
    const {checkPermission} = useAccessControl();
    const today = moment();
    const {t} = useTranslation("machine.details");
    const {t: tOffline} = useTranslation("offline");
    // For the "constant" namespace
    const {t: tConstant} = useTranslation("constant");
    const {projectId} = useParams<{ projectId: Project['id'] }>();
    const [openOperatingTimeDialog, setOpenOperatingTimeDialog] = useState<boolean>(false);
    const [openAddIssueDialog, setAddIssueDialog] = useState<boolean>(false);
    const [issueType, setIssueType] = useState<AddIssueProps['type']>('issue');
    const machine = useStoreState(state => state.machine.data);
    const nextService = useStoreState(state => state.machine.data.nextService);
    const openFromMachine = useStoreActions(actions => actions.periodicCheck.openFromMachine);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const [operatingUnitType, setOperatingUnitType] = useState<OperatingUnitTypePayload>(0);
    const fetching = useStoreState(state => state.machine.fetching);
    const setShowCompleted = useStoreActions(actions => actions.issue.setShowCompleted);

    const lastCheckStatus = useCallback((lastCheck: LastPeriodicCheck | undefined): ItemProps['subTitle'] => {
        if (lastCheck && lastCheck.date) {
            if (lastCheck.status === PERIODIC_CHECK_DRAFT_STATUS) {
                return {
                    name: t('periodicCheckDraft', { date: moment(lastCheck.date) }),
                    checked: false,
                    className: ['periodic-check-subtitle', 'pending']
                };
            }
            if (moment(lastCheck.date).isSame(today, 'day')) {
                return {name: t('periodicCheckToday'), checked: true};
            }
            return {
                name: t('periodicCheckPast', {date: moment(lastCheck.date)}),
                checked: false,
                className: ['periodic-check-subtitle', 'pending']
            };
        }
        return null;

    }, [t, today])

    /** Latest periodic check across main unit and additional equipment (same UX as parent-only). */
    const latestPeriodicCheck = useMemo(() => {
        const checks: LastPeriodicCheck[] = [];
        if (machine.lastDailyCheck?.date) {
            checks.push(machine.lastDailyCheck);
        }
        machine.linkedMachines?.forEach(linked => {
            if (linked.lastDailyCheck?.date) {
                checks.push(linked.lastDailyCheck);
            }
        });
        if (checks.length === 0) {
            return null;
        }
        return checks.reduce((latest, check) =>
            Date.parse(check.date ?? '') > Date.parse(latest.date ?? '')
                ? check
                : latest
        );
    }, [machine.lastDailyCheck, machine.linkedMachines]);
    const latestPeriodicCheckDate = latestPeriodicCheck?.date ?? null;

    const periodicCheckDoneToday = useMemo(
        () => !!latestPeriodicCheckDate && moment(latestPeriodicCheckDate).isSame(moment(), 'day'),
        [latestPeriodicCheckDate]
    );

    const periodicCheckCompletedToday = periodicCheckDoneToday
        && latestPeriodicCheck?.status !== PERIODIC_CHECK_DRAFT_STATUS
        && !latestPeriodicCheck?.pendingSync;

    const pendingTargets = useMemo(() => ([
        { id: machine.id, inspectionType: machine.inspectionType },
        { id: machine.id, inspectionType: InspectionTypes.ACCEPTANCE_INSPECTION },
        ...(machine.linkedMachines ?? []).map(linked => ({
            id: linked.id,
            inspectionType: linked.inspectionType,
        })),
    ]), [machine.id, machine.inspectionType, machine.linkedMachines]);
    const { pending: pendingChecks, loaded: pendingChecksLoaded } = usePendingPeriodicChecks(
        pendingTargets,
        `${netWorkStatus}:${machine.lastDailyCheck?.pendingSync ?? ''}:${machine.lastAcceptanceInspection?.pendingSync ?? ''}:${(machine.linkedMachines ?? []).map(linked => linked.lastDailyCheck?.pendingSync ?? '').join(',')}`,
    );
    const periodicCheckAwaitingSync = [machine, ...(machine.linkedMachines ?? [])].some(item =>
        isPeriodicCheckAwaitingSync(
            pendingChecks,
            pendingChecksLoaded,
            item.id,
            item.inspectionType,
            item.lastDailyCheck?.pendingSync,
        )
    );
    const acceptanceAwaitingSync = isPeriodicCheckAwaitingSync(
        pendingChecks,
        pendingChecksLoaded,
        machine.id,
        InspectionTypes.ACCEPTANCE_INSPECTION,
        machine.lastAcceptanceInspection?.pendingSync,
    );
    const { operatingTime: operatingTimeAwaitingSync, operatingCount: operatingCountAwaitingSync } = usePendingOperatingData(machine.id);

    const toggleOperatingDataDialog = useCallback((type: OperatingUnitTypePayload) => {
        setOpenOperatingTimeDialog(!openOperatingTimeDialog);
        setOperatingUnitType(type);
    }, [setOpenOperatingTimeDialog, openOperatingTimeDialog, setOperatingUnitType]);

    const toggleAddIssueDialog = useCallback(() => {
        setAddIssueDialog(!openAddIssueDialog);
    }, [setAddIssueDialog, openAddIssueDialog]);

    const openIssueDialog = useCallback(() => {
        setIssueType('issue');
        toggleAddIssueDialog();
    }, [setIssueType, toggleAddIssueDialog]);

    const openRepairDialog = useCallback(() => {
        setIssueType('repair');
        toggleAddIssueDialog();
    }, [setIssueType, toggleAddIssueDialog]);

    const openPeriodicCheck = useCallback(async (inspectionType, lastRegisteredCheck, equipment?: typeof machine) => {
        const source = equipment ?? machine;
        await openFromMachine({
            equipment: source,
            projectId,
            inspectionType,
            lastRegisteredCheck,
        });
        history.push(route(MACHINES_DETAILS_ROUTES.periodicCheck, {id: machine.id, projectId}));
    }, [history, openFromMachine, machine, projectId]);

    const showChecks = useCallback((inspectionType, lastRegisteredCheck, equipment?: typeof machine) => {
        openPeriodicCheck(inspectionType, lastRegisteredCheck, equipment);
    }, [openPeriodicCheck]);

    const nextServiceInfo = useMemo(() => {
        const service: any = {
            subtitle: {name:'-'},
            status: null
        }
        if(nextService?.status){
            service.subtitle = {name: `${tConstant('nextService')} : ${nextService.dueHours}`}
            service.status = {name:`Status: ${tConstant(`${nextService.workOrderStatus}`)}`}
        }

        return service
    },[nextService, tConstant])

    const items = useMemo<ItemProps[]>(() => ([
        {
            icon: 'pending_actions',
            title: t('serviceInfo'),
            subTitle: nextServiceInfo.subtitle,
            thirdTitle: nextServiceInfo.status,
            rightIcon: 'keyboard_arrow_right',
            className: 'bottom-space',
            url: route(MACHINES_DETAILS_ROUTES.services, {id: machine.id}),
            requiredPermissions: PERMISSION_GROUPS.readServiceDetails
        },
        ...!!machine.isOperatingTimeVisible ? [{
            icon: 'speed',
            title: t(machine.categoryType === 'vehicle' ? 'operatingKm' : 'operatingTime'),
            subTitle: {
                name: t((moment(machine.lastOperatingTimeDate).isSame(moment(), 'day') ? 'operatingTimeToday' : 'operatingTimePast'), {
                    operatingTime: machine.operatingTime,
                    type: machine.operatingType
                }),
                checked: moment(machine.lastOperatingTimeDate).isSame(moment(), 'day')
            },
            thirdTitle: operatingTimeAwaitingSync
                ? { name: tOffline('awaitingSynchronization'), checked: true }
                : null,
            rightIcon: 'add',
            ...!checkPermission(PERMISSION_GROUPS.readWritePeriodicCheck) && (!checkPermission(PERMISSION_GROUPS.readWriteOperatingCount) || !machine.isOperatingCountVisible) && {className: 'bottom-space'},
            onSelect: () => toggleOperatingDataDialog(OperatingTypes.OPERATING_TIME),
            requiredPermissions: PERMISSION_GROUPS.readWriteOperatingTime
        }] : [],
        ...!!machine.isOperatingCountVisible ? [{
            icon: 'sensors',
            title: t('operatingCount'),
            subTitle: {
                name: t((moment(machine.lastOperatingCountDate).isSame(moment(), 'day') ? 'operatingCountToday' : 'operatingCountPast'), {
                    operatingCount: machine.operatingCount,
                    type: OPERATING_COUNT_UNIT_NAME
                }),
                checked: moment(machine.lastOperatingCountDate).isSame(moment(), 'day')
            },
            thirdTitle: operatingCountAwaitingSync
                ? { name: tOffline('awaitingSynchronization'), checked: true }
                : null,
            rightIcon: 'add',
            ...!checkPermission(PERMISSION_GROUPS.readWritePeriodicCheck) && {className: 'bottom-space'},
            onSelect: () => toggleOperatingDataDialog(OperatingTypes.OPERATING_COUNT),
            requiredPermissions: PERMISSION_GROUPS.readWriteOperatingCount
        }] : [],
        {
            icon: !latestPeriodicCheckDate || periodicCheckCompletedToday || periodicCheckAwaitingSync ? 'event_available' : 'event_busy',
            title: t('periodicCheck'),
            subTitle: latestPeriodicCheckDate || periodicCheckAwaitingSync ? {
                name: periodicCheckAwaitingSync
                    ? tOffline('awaitingSynchronization')
                    : latestPeriodicCheck?.status === PERIODIC_CHECK_DRAFT_STATUS
                    ? t('periodicCheckDraft', { date: moment(latestPeriodicCheckDate) })
                    : periodicCheckDoneToday
                        ? t('periodicCheckToday')
                        : t('periodicCheckPast', { date: moment(latestPeriodicCheckDate) }),
                checked: periodicCheckCompletedToday || periodicCheckAwaitingSync,
                className: ['periodic-check-subtitle', latestPeriodicCheckDate && !periodicCheckCompletedToday && !periodicCheckAwaitingSync ? 'pending' : '']
            } : null,
            rightIcon: 'add',
            className: ['bottom-space', 'periodic-check', (latestPeriodicCheckDate || periodicCheckAwaitingSync) ? ((periodicCheckCompletedToday || periodicCheckAwaitingSync) ? 'completed' : 'pending') : ''],
            onSelect: () => (machine.linkedMachines && machine.linkedMachines.length > 0) 
                ? history.push(route(MACHINES_DETAILS_ROUTES.periodicCheckEquipments, {id: machine.id, projectId}))
                : showChecks(machine.inspectionType, machine.lastDailyCheck, machine),
            requiredPermissions: PERMISSION_GROUPS.readWritePeriodicCheck
        },
        {
            icon: 'error_outline',
            title: t('errorStatus'),
            rightIcon: 'keyboard_arrow_right',
            ...!checkPermission(PERMISSION_GROUPS.registerIssues) && !checkPermission(PERMISSION_GROUPS.registerRepairByDriver) && {className: 'bottom-space'},
            subTitle: {name: t('noOpenErrors')},
            statuses: machine.issuesCount,
            url: route(MACHINES_DETAILS_ROUTES.issues, {id: machine.id}),
            requiredPermissions: PERMISSION_GROUPS.readIssues
        },
        {
            icon: 'report_problem',
            title: t('registerError'),
            rightIcon: 'add',
            ...!checkPermission(PERMISSION_GROUPS.registerRepairByDriver) && {className: 'bottom-space'},
            onSelect: openIssueDialog,
            requiredPermissions: PERMISSION_GROUPS.registerIssues
        },
        {
            icon: 'build',
            title: t('registerRepairByDriver'),
            rightIcon: 'add',
            className: 'bottom-space',
            onSelect: openRepairDialog,
            requiredPermissions: PERMISSION_GROUPS.registerRepairByDriver
        },
        {
            icon: 'assignment',
            title: t('receptionCheck'),
            subTitle: acceptanceAwaitingSync
                ? { name: tOffline('awaitingSynchronization'), checked: true }
                : lastCheckStatus(machine.lastAcceptanceInspection),
            rightIcon: 'add',
            className: 'bottom-space',
            onSelect: () => showChecks(InspectionTypes.ACCEPTANCE_INSPECTION, machine.lastAcceptanceInspection, machine),
            requiredPermissions: PERMISSION_GROUPS.performAcceptanceInspection
        },
        ...(netWorkStatus === 0 ? [{
            icon: 'add',
            title: t('registerTraining'),
            rightIcon: 'keyboard_arrow_right',
            url: route(MACHINES_DETAILS_ROUTES.registerTraining, {id: machine.id}),
            requiredPermissions: PERMISSION_GROUPS.registerTraining
        }] : []),
        ...(netWorkStatus === 0 ? [{
            icon: 'list',
            title: t('listTraining'),
            rightIcon: 'keyboard_arrow_right',
            className: 'bottom-space',
            url: route(MACHINES_DETAILS_ROUTES.listTraining, {id: machine.id}),
            requiredPermissions: PERMISSION_GROUPS.training
        }] : []),
        ...(netWorkStatus === 0 ? [{
            icon: 'forum',
            title: t('communication.title'),
            subTitle: {name: t('communication.description')},
            rightIcon: 'keyboard_arrow_right',
            className: 'bottom-space',
            url: route(MACHINES_DETAILS_ROUTES.communication, {id: machine.id, projectId}),
            requiredPermissions: PERMISSION_GROUPS.machineRegistration
        }] : []),
        {
            icon: 'error_outline',
            title: t('informationAndDocuments.title'),
            subTitle: {name: t('informationAndDocuments.description')},
            rightIcon: 'keyboard_arrow_right',
            url: route(MACHINES_DETAILS_ROUTES.documentation, {id: machine.id, projectId}),
            requiredPermissions: PERMISSION_GROUPS.readMachineDocuments
        },
        //{icon: 'assignment', title: t('receptionCheck'), rightIcon: 'add'},
    ]), [
        history,
        machine,
        openRepairDialog,
        toggleOperatingDataDialog,
        lastCheckStatus,
        latestPeriodicCheckDate,
        periodicCheckDoneToday,
        periodicCheckCompletedToday,
        periodicCheckAwaitingSync,
        acceptanceAwaitingSync,
        operatingTimeAwaitingSync,
        operatingCountAwaitingSync,
        latestPeriodicCheck,
        openIssueDialog,
        showChecks,
        checkPermission,
        t,
        tOffline,
        projectId,
        nextServiceInfo,
        netWorkStatus
    ]);
    
    useDidMountEffect(() => {
        setShowCompleted(false);
    });
    
    if (fetching) {
        return <Loader size="32" />;
    }

    return (
        <>
            <Header/>
            <List list={items}/>
            <RegisterOperatingTime open={openOperatingTimeDialog} onClose={() => toggleOperatingDataDialog(operatingUnitType)} operatingUnitType={operatingUnitType} />
            <AddIssue open={openAddIssueDialog} onClose={toggleAddIssueDialog} type={issueType} />  
        </>
    );
};

export default Registration
